import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wrench, Plus } from "lucide-react";
import ProductionTable from "@/components/ProductionTable";
import { EditWorkOrderDialog } from "./EditWorkOrderDialog";
import { StageFilterChips, stageFilterApply, STAGE_FILTERS } from "@/components/StageFilterChips";
import { loc } from "@/lib/numerals";

function CreateWorkOrderDialog({ spareItems, onClose, onCreate }) {
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState("10");
  const [due, setDue] = useState(() => new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 16));
  const [saving, setSaving] = useState(false);
  const picked = spareItems.find((s) => s.id === itemId);
  const submit = async () => {
    if (!itemId || !(Number(qty) > 0)) return;
    setSaving(true);
    try {
      await onCreate({ product_name: picked?.name || "Mfg. Spare", bom_item_id: itemId, target_quantity: Number(qty), priority: "Medium", due_date: new Date(due).toISOString() });
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="wo-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wrench className="h-4 w-4 text-indigo-600" /> New Work Order</DialogTitle>
          <DialogDescription className="text-xs">Manufacture a spare part in-house. Materials reserve when the first stage starts and the finished part is added to stock when the final stage completes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Mfg. Spares</Label>
            <SearchableSelect
              value={itemId}
              onValueChange={setItemId}
              placeholder="Select a manufactured spare part…"
              searchPlaceholder="Search spare parts…"
              emptyText="No manufactured spare parts. Create one in the BOM tab first."
              options={spareItems.map((s) => ({ value: s.id, label: `${s.name} (${s.sku})`, keywords: `${s.name} ${s.sku}` }))}
              testid="wo-item-select"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="wo-qty" />
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="wo-due" />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={!itemId || !(Number(qty) > 0) || saving} onClick={submit} data-testid="wo-create-submit">
            {saving ? "Creating…" : "Create Work Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Work Orders tab — a visual clone of the Schedule's Production Health table (same rows, WIP pipeline,
// stage boxes and colour-coding), scoped to Manufactured Spare Parts built in-house.
export default function WorkOrdersPanel({ workOrders = [], bomItems = [], role, canManage = false, isAdmin = false, currentUsername, onApplyBomRefresh, onAcknowledgeBom, onCreate, onEditWorkOrder, onLogStage, onShipStage, onReceiveStage, onHistory, onDelete, onOpenOrder }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const spareItems = bomItems.filter((b) => b.item_type === "mfg_spare" && b.status !== "archived" && (b.approval_status || "Active") === "Active");
  const filtered = stageFilterApply(workOrders, stageFilter);
  const active = workOrders.filter((w) => !["Completed", "Cancelled"].includes(w.status)).length;
  const headerActions = canManage ? (
    <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="btn-add-work-order">
      <Plus className="h-4 w-4 mr-1" /> Create Work Order
    </Button>
  ) : null;
  return (
    <div data-testid="work-orders-panel" className="space-y-3">
      <StageFilterChips orders={workOrders} value={stageFilter} onChange={setStageFilter} containerTestid="wo-stage-filters" itemPrefix="wo-filter" />
      <ProductionTable
        orders={filtered}
        machines={[]}
        role={role}
        sortByStatus="schedule"
        onEdit={(o) => setEditing(o)}
        spotlightOverdue
        isAdmin={isAdmin}
        currentUsername={currentUsername}
        onApplyBomRefresh={onApplyBomRefresh}
        onAcknowledgeBom={onAcknowledgeBom}
        onOpenOrder={onOpenOrder}
        onDelete={onDelete}
        onLogStage={onLogStage}
        onShipStage={onShipStage}
        onReceiveStage={onReceiveStage}
        onHistory={onHistory}
        title="Work Orders"
        subtitle={`${loc(String(active))} active · ${loc(String(workOrders.length))} total · Mfg. Spares built in-house — click an order to expand its WIP pipeline`}
        headerActions={headerActions}
        emptyText={stageFilter === "all" ? "No work orders yet. Raised automatically when a production order needs a manufactured spare part, or manually with Create Work Order." : `No work orders currently waiting at ${STAGE_FILTERS.find((f) => f.key === stageFilter)?.label}.`}
      />
      {createOpen && <CreateWorkOrderDialog spareItems={spareItems} onClose={() => setCreateOpen(false)} onCreate={onCreate} />}
      {editing && <EditWorkOrderDialog order={editing} onClose={() => setEditing(null)} onSave={onEditWorkOrder} />}
    </div>
  );
}
